import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { WeekPlan } from '../../core/types/weekPlan';
import { NoSpinnerInput } from './NoSpinnerInput';
import { CustomCell } from './CustomCell';
/* eslint-disable react/jsx-props-no-spreading */

type Props = {
  data: WeekPlan[];
  handleRowUpdate: (newRow: WeekPlan) => Promise<WeekPlan>;
  isEditable: boolean;
};

export const renderColoredCell = (params: any, color: string) => (
  <span
    style={{
      width: '100%',
      height: '100%',
      backgroundColor: color,
      padding: '1rem',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    }}
  >
    {params.formattedValue}
  </span>
);

const WeekPlannerTable = ({ data, handleRowUpdate, isEditable }: Props) => {
  const renderCell = (params: any) => <CustomCell {...params} />;

  const columns: GridColDef[] = [
    {
      field: 'isActive',
      headerName: 'Pågående',
      flex: 2,
      type: 'boolean',
      editable: true,
    },
    {
      field: 'projectName',
      headerName: 'Prosjekt',
      flex: 4,
      type: 'string',
      renderCell: (params) => (
        <CustomCell
          {...params}
          formattedValue={`${params.row.projectId} ${params.row.projectName}`}
        />
      ),
    },
    {
      field: 'site',
      headerName: 'Anlegg',
      flex: 2,
      type: 'string',
      renderCell,
    },
    {
      field: 'mondayCars',
      headerName: 'Man',
      flex: 1,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell,
    },
    {
      field: 'tuesdayCars',
      headerName: 'Tir',
      flex: 1,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell,
    },
    {
      field: 'wednesdayCars',
      headerName: 'Ons',
      flex: 1,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell,
    },
    {
      field: 'thursdayCars',
      headerName: 'Tor',
      flex: 1,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell,
    },
    {
      field: 'fridayCars',
      headerName: 'Fre',
      flex: 1,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell,
    },
    {
      field: 'comment',
      headerName: 'Kommentar',
      flex: 5,
      sortable: false,
      editable: true,
      type: 'string',
      renderCell,
    },
    {
      field: 'readyMade',
      headerName: 'Ferdigvare',
      flex: 2,
      sortable: false,
      editable: true,
      type: 'number',
      renderCell: (params) => renderColoredCell(params, '#d6f5d6'),
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
    },
    {
      field: 'environmentalReadyMade',
      headerName: 'Miljø FV',
      flex: 2,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell: (params) => renderColoredCell(params, '#d6f5d6'),
    },
    {
      field: 'blastedRockIn',
      headerName: 'Sprenst',
      flex: 2,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell: (params) => renderColoredCell(params, '#d6f5d6'),
    },
    {
      field: 'mixedQuality',
      headerName: 'Sams',
      flex: 2,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell: (params) => renderColoredCell(params, '#e3f2fd'),
    },
    {
      field: 'dumping',
      headerName: 'Tippmasse',
      flex: 2,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell: (params) => renderColoredCell(params, '#e3f2fd'),
    },
    {
      field: 'washing',
      headerName: 'Vaskemasse',
      flex: 2,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell: (params) => renderColoredCell(params, '#e3f2fd'),
    },
    {
      field: 'contaminated',
      headerName: 'Foruma',
      flex: 2,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell: (params) => renderColoredCell(params, '#e3f2fd'),
    },
    {
      field: 'blastedRockOut',
      headerName: 'Sprenst',
      flex: 2,
      sortable: false,
      editable: true,
      type: 'number',
      renderEditCell: (params) => <NoSpinnerInput {...params} />,
      renderCell: (params) => renderColoredCell(params, '#e3f2fd'),
    },
    {
      field: 'totalMassIn',
      headerName: 'Inn',
      flex: 2.5,
      type: 'number',
      sortable: false,
      renderCell,
    },
    {
      field: 'totalMassOut',
      headerName: 'Ut',
      flex: 2.5,
      type: 'number',
      sortable: false,
      renderCell,
    },
    {
      field: 'remainingMassIn',
      headerName: 'Inn',
      flex: 2.5,
      type: 'number',
      sortable: false,
      renderCell,
    },
    {
      field: 'remainingMassOut',
      headerName: 'Ut',
      flex: 2.5,
      type: 'number',
      sortable: false,
      renderCell,
    },
  ];

  const columnGrouping = [
    {
      groupId: 'Prosjekt',
      children: [{ field: 'projectName' }, { field: 'isActive' }],
    },
    {
      groupId: 'Biler',
      children: [
        { field: 'mondayCars' },
        { field: 'tuesdayCars' },
        { field: 'wednesdayCars' },
        { field: 'thursdayCars' },
        { field: 'fridayCars' },
        { field: 'comment' },
      ],
    },
    {
      groupId: 'Masser (m3)',
      children: [
        { field: 'readyMade' },
        { field: 'environmentalReadyMade' },
        { field: 'blastedRockIn' },
        { field: 'blastedRockOut' },
        { field: 'mixedQuality' },
        { field: 'dumping' },
        { field: 'washing' },
        { field: 'contaminated' },
      ],
    },
    {
      groupId: 'Totalt (tonn)',
      children: [{ field: 'totalMassIn' }, { field: 'totalMassOut' }],
    },
    {
      groupId: 'Rest (tonn)',
      children: [{ field: 'remainingMassIn' }, { field: 'remainingMassOut' }],
    },
  ];

  return (
    <Box height="100%" sx={{ width: '100%' }}>
      <DataGrid
        rows={data}
        columns={columns}
        getRowId={(row) => row.projectId + (row.site || '')}
        experimentalFeatures={{ columnGrouping: true, newEditingApi: true }}
        processRowUpdate={handleRowUpdate}
        columnGroupingModel={columnGrouping}
        editMode="row"
        disableColumnMenu
        isCellEditable={() => isEditable}
        hideFooter
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-cell': {
            padding: 0,
          },
        }}
      />
    </Box>
  );
};

export default WeekPlannerTable;
